import { useState, useEffect, useCallback } from "react";
import { logger } from "../../../../utils/logger";
import {
  setupCamera,
  cleanupCamera,
  loadJsQRLibrary,
  scanQRFromVideo,
  processQRContent,
} from "../utils/cameraUtils";

/**
 * Hook for managing QR code scanning functionality
 * @param {boolean} isOpen - Whether the dialog is open
 * @param {Function} onQRScanned - Callback when QR code is successfully scanned
 * @returns {Object} QR scanning state and handlers
 */
export const useQRScanner = (isOpen, onQRScanned) => {
  const [scanned, setScanned] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [videoReady, setVideoReady] = useState(false);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setScanned(false);
      setCameraError(null);
      setVideoReady(false);
    } else {
      // Clean up camera when dialog closes
      cleanupCamera();
    }
  }, [isOpen]);

  // Handle QR code detection
  const handleQRDetected = useCallback(
    (qrText) => {
      if (!qrText) return;

      setScanned(true);
      const processedText = processQRContent(qrText);
      onQRScanned(processedText);
    },
    [onQRScanned]
  );

  // Start QR code scanning process
  const startQRScanning = useCallback(
    async (video) => {
      try {
        // Load jsQR library
        await loadJsQRLibrary();

        // Create canvas for QR processing
        const canvas = document.createElement("canvas");
        canvas.width = 1080;
        canvas.height = 720;

        // Scanning parameters
        let scanDelay = 150;
        let scanCount = 0;

        const performScan = () => {
          if (scanned) return false; // Stop scanning if already scanned

          try {
            const code = scanQRFromVideo(video, canvas);

            if (code && code.data) {
              handleQRDetected(code.data);
              return false; // Stop scanning
            }

            return true; // Continue scanning
          } catch (e) {
            logger.error("QR scan error:", e);
            return true; // Continue scanning despite error
          }
        };

        // Initial fast scanning
        const scanInterval = setInterval(() => {
          scanCount++;

          if (!performScan()) {
            clearInterval(scanInterval);
            return;
          }

          // After 10 attempts, scan less frequently to save CPU
          if (scanCount > 10 && scanDelay < 500) {
            scanDelay = 500;
            clearInterval(scanInterval);

            const slowInterval = setInterval(() => {
              if (!performScan()) {
                clearInterval(slowInterval);
              }
            }, scanDelay);
          }
        }, scanDelay);
      } catch (error) {
        logger.error("Failed to start QR scanning:", error);
        setCameraError("Failed to initialize QR scanner");
      }
    },
    [scanned, handleQRDetected]
  );

  // Initialize camera and QR scanning
  const initializeCamera = useCallback(
    async (containerElement) => {
      if (!containerElement || !isOpen || videoReady || cameraError) {
        return;
      }

      try {
        // Set up video element
        const video = document.createElement("video");
        video.style.width = "100%";
        video.style.height = "100%";
        video.style.objectFit = "cover";
        video.playsInline = true;

        // Set up camera stream
        await setupCamera(
          (stream) => {
            video.srcObject = stream;
            setVideoReady(true);

            // Add video to container
            containerElement.innerHTML = "";
            containerElement.appendChild(video);

            // Play after added to DOM
            setTimeout(() => {
              video
                .play()
                .catch((err) => logger.error("Video play failed:", err));
            }, 100);

            // Start QR scanning
            startQRScanning(video);
          },
          (error) => {
            setCameraError(error);
          }
        );
      } catch (error) {
        setCameraError(error.message || "Camera initialization failed");
      }
    },
    [isOpen, videoReady, cameraError, startQRScanning]
  );

  return {
    scanned,
    cameraError,
    videoReady,
    initializeCamera,
  };
};
